import { 
  FileBarChart, 
  MousePointer2, 
  Calendar,
  Building2,
  ArrowRight,
  ChartBar
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function InitialScreen() {
  return (
    <div className="flex h-[70vh] w-full items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        {/* Icon Display */}
        <div className="relative mx-auto w-fit">
          {/* Background circles */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-48 w-48 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 blur-2xl" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-32 w-32 rounded-full bg-gradient-to-tr from-primary/20 to-transparent animate-pulse" />
          </div>
          
          {/* Main icon container */}
          <div className="relative flex h-40 w-40 items-center justify-center rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 shadow-xl backdrop-blur-sm">
            <FileBarChart className="h-20 w-20 text-primary" />
            
            {/* Floating elements */}
            <div className="absolute -right-6 -top-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
              <ChartBar className="h-6 w-6 text-emerald-500" />
            </div>
            <div className="absolute -bottom-4 -left-4 flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
              <Building2 className="h-5 w-5 text-blue-500" />
            </div>
            <div className="absolute -right-4 bottom-2 flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
              <Calendar className="h-5 w-5 text-amber-500" />
            </div>
          </div>
        </div>
        
        {/* Title and description */}
        <div className="text-center space-y-4">
          <h3 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            تقرير توريد أصناف الشركات
          </h3>
          <p className="text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
            يرجى تحديد اسم الشركة والفترة الزمنية المطلوبة لعرض تقرير مفصل عن حركة التوريد
          </p>
        </div>
        
        {/* Instructions cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="group relative overflow-hidden border-slate-200 bg-white p-4 transition-all hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative space-y-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">1. اختر الشركة</h4>
                <p className="text-xs text-slate-600 dark:text-slate-400">حدد الشركة المطلوبة من القائمة</p>
              </div>
            </div>
          </Card>
          
          <Card className="group relative overflow-hidden border-slate-200 bg-white p-4 transition-all hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative space-y-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">2. حدد الفترة</h4>
                <p className="text-xs text-slate-600 dark:text-slate-400">اختر تاريخ البداية والنهاية</p>
              </div>
            </div>
          </Card>
          
          <Card className="group relative overflow-hidden border-slate-200 bg-white p-4 transition-all hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative space-y-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <MousePointer2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">3. اضغط بحث</h4>
                <p className="text-xs text-slate-600 dark:text-slate-400">انقر على زر البحث لعرض النتائج</p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Status indicator */}
        <div className="flex items-center justify-center gap-3">
          <Badge variant="outline" className="h-8 px-4 gap-2">
            <div className="h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
            في انتظار الاختيار
          </Badge>
          <ArrowRight className="h-4 w-4 text-slate-400" />
          <Badge variant="soft" className="h-8 px-4 gap-2 opacity-50">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            جاهز للعرض
          </Badge>
        </div>
      </div>
    </div>
  );
}
